import { Link } from '@reach/router';
import { AppPath } from 'appConstants';
import Button from 'components/button/Button';
import FormTextField from 'components/form/FormTextField';
import { PasswordHelper } from 'components/passwordHelper/PasswordHelper';
import { PasswordStrengthMeter } from 'components/passwordStrengthMeter/PasswordStrengthMeter';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import Icon_Form_01 from 'assets/ibanera/Icon_Form_01.png';
import { RegistrationStepProps, AccountDetails, SignUpFormData } from './models';
import * as Yup from 'yup';
import { FormPhoneNumberField } from 'components/form/FormPhoneNumberField';
import instance from 'api';
import { endpoints } from 'endpoints.config';
import { Toast, ToastMessageReason } from 'helpers/toast';

export type SignUpFormProps = RegistrationStepProps & {
    setAccountDetails: React.Dispatch<React.SetStateAction<AccountDetails>>;
    setSignUpFormData: React.Dispatch<React.SetStateAction<SignUpFormData | null>>;
    onBack?: () => void;
    signUpFormData?: SignUpFormData | null;
};

export function SignUpForm({
    translations,
    onComplete,
    setAccountDetails,
    setSignUpFormData,
    signUpFormData,
    onBack,
}: SignUpFormProps) {
    const [showPasswordHelper, setShowPasswordHelper] = useState(false);
    const [passwordVisibility, setPasswordVisibility] = useState(false);

    const handleSubmit = async (values: typeof initialValues) => {
        try {
            await instance.post(endpoints.registration.verifyEmail, { email: values.emailAddress }).then(() => {
                setAccountDetails({
                    emailAddress: values.emailAddress,
                    password: values.password,
                    phoneNumber: values.phoneNumber,
                });
                setSignUpFormData({
                    firstName: values.firstName,
                    middleNames: values.middleNames,
                    lastName: values.lastName,
                    emailAddress: values.emailAddress,
                    phoneNumber: values.phoneNumber,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                });
                onComplete();
            })
        } catch (error: any) {
            const errField = error?.response?.data?.errors[0]?.fieldName
            if (errField === "EmailAddress") {
                Toast.openToastMessage(
                    'Email Address already in use',
                    ToastMessageReason.ERROR
                );
            }
        }
    };

    const validationSchema = Yup.object({
        firstName: Yup.string().required('Please provide a first name'),
        lastName: Yup.string().required('Please provide a last name'),
        emailAddress: Yup.string()
            .email(translations?.email_address_required)
            .required(translations?.email_address_required),
        phoneNumber: Yup.string().required('Required'),
        password: Yup.string()
            .required(translations?.password_required)
            .min(8, 'Password must be at least 8 characters long')
            .matches(/[0-9]/, 'Password must contain a number')
            .matches(/[a-zA-Z]/, 'Password must contain a letter'),
        confirmPassword: Yup.string()
            .required('Please confirm the given password')
            .test('passwords-match', 'Passwords must match', (value, ctx) => ctx.parent.password === value),
    });

    const initialValues = useMemo(
        () =>
            signUpFormData ?? {
                firstName: '',
                middleNames: '',
                lastName: '',
                emailAddress: '',
                phoneNumber: '',
                password: '',
                confirmPassword: '',
            },
        [signUpFormData]
    );


    return (
        <>
            {translations && (
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    validationSchema={validationSchema}
                >
                    {({ setFieldValue, setFieldTouched }) => (
                        <Form className="RegisterFormWidth">
                            <div className="RegisterFormWidth">
                                {onBack && (
                                    <Button
                                        priority="tertiary"
                                        className="ChangeNumberButton InlineLink"
                                        onClick={onBack}
                                        type="button"
                                    >
                                        &lt; Back
                                    </Button>
                                )}
                                <h1>Sign Up</h1>
                                <p>
                                    Open your account today. To get started, please enter the name
                                    of the person who will be managing this account.
                                </p>
                                <div className="FormSideBySide">
                                    <FormTextField
                                        field="firstName"
                                        label="First name"
                                        required
                                        maxLength={200}
                                        sideBySide
                                    />
                                    <FormTextField
                                        field="middleNames"
                                        label="Middle Name"
                                        required={false}
                                        maxLength={200}
                                        sideBySide
                                    />
                                    <FormTextField
                                        field="lastName"
                                        label="Last name"
                                        required
                                        maxLength={200}
                                        sideBySide
                                    />
                                </div>
                                <div className="FormSideBySide">
                                    <FormTextField
                                        field="emailAddress"
                                        label={translations.email_address}
                                        required
                                        maxLength={200}
                                        sideBySide
                                        inputMode="email"
                                    />
                                    <FormPhoneNumberField
                                        field="phoneNumber"
                                        label="Phone Number"
                                        sideBySide
                                    />
                                </div>
                                <div className="FormBox PasswordField">
                                    <FormTextField
                                        field="password"
                                        label={translations.password}
                                        required
                                        maxLength={20}
                                        type={passwordVisibility ? 'text' : 'password'}
                                        showIcon
                                        icon={Icon_Form_01}
                                        hint="8+ characters, must contain numbers and letters"
                                        onClickIcon={() => setPasswordVisibility((s) => !s)}
                                        onBlur={(e) => {
                                            setFieldValue('password', e.target.value.trim(), true);
                                            setFieldTouched('password', true);
                                        }}
                                    />
                                    <PasswordStrengthMeter
                                        fieldName="password"
                                        onInfoClick={() => setShowPasswordHelper((s) => !s)}
                                        userInputs={['ibanera']}
                                    />
                                    {showPasswordHelper && (
                                        <PasswordHelper
                                            fieldName="password"
                                            userInputs={['ibanera']}
                                        />
                                    )}
                                </div>
                                <FormTextField
                                    field="confirmPassword"
                                    label="Confirm password"
                                    required
                                    maxLength={20}
                                    type="password"
                                    onBlur={(e) => {
                                        setFieldValue('confirmPassword', e.target.value.trim(), true);
                                        setFieldTouched('confirmPassword', true);
                                    }}
                                />
                                <Button
                                    priority="primary"
                                    variety="full"
                                    type="submit"
                                >
                                    {translations.continue_button}
                                </Button>
                                <p className="RegisterFormRedirect">
                                    Already have an account?{' '}
                                    <Link to={AppPath.SIGN_IN} className="FormLink">
                                        {translations.sign_in_button}
                                    </Link>
                                </p>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
}
